import { useCallback, useEffect, useMemo, useState } from 'react';
import { FIELDS_TO_MASSIVE_ASSIGN } from 'reducers/reducer';

const useMassAssignerTools = ({
  processedProperties,
  fieldsToMassiveAssign,
  dispatch,
  isSortByCrop
}) => {
  const [allPropertiesSelected, setAllPropertiesSelected] = useState(false);

  // Pre-compute a Map<fieldId, Set<cropzoneId>> for O(1) selection lookups
  const selectedFieldMap = useMemo(() => {
    const map = new Map();
    fieldsToMassiveAssign.forEach(f => {
      map.set(f.id, new Set(f.cropzones.map(cz => cz.id)));
    });
    return map;
  }, [fieldsToMassiveAssign]);

  // Helper Functions
  const addCropZonesToExistingField = (existingField, cropZonesToAdd) => {
    const existingCZIds = new Set(existingField.cropzones.map(cz => cz.id));
    cropZonesToAdd.forEach(cz => {
      if (!existingCZIds.has(cz.id)) {
        existingField.cropzones.push(cz);
      }
    });
  };

  const createFieldEntry = (field, farm, cropZones) => ({
    ...field,
    farmName: farm.name,
    farmId: farm.id,
    cropZones
  });

  const getAllFieldsGroupedByFarm = properties => {
    const allFields = [];
    properties.forEach(farm => {
      farm.fields.forEach(field => {
        allFields.push(createFieldEntry(field, farm, [...field.cropzones]));
      });
    });
    return allFields;
  };

  const addFieldCropZonesByCrop = (allFields, field, farm, crop) => {
    const cropZonesToAdd = field.cropzones.filter(cz => cz.crop.id === crop.id);
    if (cropZonesToAdd.length === 0) return;
    const existingFieldIndex = allFields.findIndex(f => f.id === field.id);
    if (existingFieldIndex >= 0) {
      addCropZonesToExistingField(
        allFields[existingFieldIndex],
        cropZonesToAdd
      );
    } else {
      allFields.push(createFieldEntry(field, farm, cropZonesToAdd));
    }
  };

  // Helper function to process fields for a crop and farm
  const processFieldsForCropAndFarm = (allFields, farm, crop) => {
    farm.fields.forEach(field => {
      addFieldCropZonesByCrop(allFields, field, farm, crop);
    });
  };

  const getAllFieldsGroupedByCrop = properties => {
    const allFields = [];
    properties.forEach(crop => {
      crop.farms.forEach(farm => {
        processFieldsForCropAndFarm(allFields, farm, crop);
      });
    });
    return allFields;
  };

  // Selection Check Functions using O(1) Map lookups
  const isFieldFullySelected = useCallback(
    field => {
      const czSet = selectedFieldMap.get(field.id);
      if (!czSet) return false;
      return field.cropzones.every(cz => czSet.has(cz.id));
    },
    [selectedFieldMap]
  );

  const isFieldSelectedInCropGroup = useCallback(
    (crop, field) => {
      const cropZonesToCheck = field.cropzones.filter(
        cz => cz.crop.id === crop.id
      );
      if (cropZonesToCheck.length === 0) return false;
      const czSet = selectedFieldMap.get(field.id);
      if (!czSet) return false;
      return cropZonesToCheck.every(cz => czSet.has(cz.id));
    },
    [selectedFieldMap]
  );

  const isFarmSelectedInCropGroup = useCallback(
    (crop, farm) =>
      farm.fields.every(field => isFieldSelectedInCropGroup(crop, field)),
    [isFieldSelectedInCropGroup]
  );

  const isCropGroupFullySelected = useCallback(
    crop => crop.farms.every(farm => isFarmSelectedInCropGroup(crop, farm)),
    [isFarmSelectedInCropGroup]
  );

  const isFarmFullySelected = useCallback(
    farm => farm.fields.every(field => isFieldFullySelected(field)),
    [isFieldFullySelected]
  );

  const handleSelectAllProperties = useCallback(() => {
    const newSelectedValue = !allPropertiesSelected;
    setAllPropertiesSelected(newSelectedValue);
    if (newSelectedValue) {
      const allFields = isSortByCrop
        ? getAllFieldsGroupedByCrop(processedProperties)
        : getAllFieldsGroupedByFarm(processedProperties);
      dispatch({
        type: FIELDS_TO_MASSIVE_ASSIGN,
        payload: allFields
      });
    } else {
      dispatch({
        type: FIELDS_TO_MASSIVE_ASSIGN,
        payload: []
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPropertiesSelected, isSortByCrop, processedProperties, dispatch]);

  useEffect(() => {
    if (!fieldsToMassiveAssign.length) {
      setAllPropertiesSelected(false);
    }
  }, [fieldsToMassiveAssign]);

  // useEffect Hook for Updating Selected All Properties State
  useEffect(() => {
    let allSelected = false;
    if (processedProperties && processedProperties.length > 0) {
      if (isSortByCrop) {
        allSelected = processedProperties.every(
          crop => crop.farms && isCropGroupFullySelected(crop)
        );
      } else {
        allSelected = processedProperties.every(
          farm => farm.fields && isFarmFullySelected(farm)
        );
      }
    }
    setAllPropertiesSelected(allSelected);
  }, [
    processedProperties,
    isSortByCrop,
    isCropGroupFullySelected,
    isFarmFullySelected
  ]);

  // Helper functions for updating fields and cropzones
  const removeCropZonesFromField = (existingField, cropId) => {
    // eslint-disable-next-line no-param-reassign
    existingField.cropzones = existingField.cropzones.filter(
      cz => cz.crop.id !== cropId
    );
  };

  const addCropZonesToField = (existingField, cropzonesToAdd) => {
    const existingCZIds = new Set(existingField.cropzones.map(cz => cz.id));
    cropzonesToAdd.forEach(cz => {
      if (!existingCZIds.has(cz.id)) {
        existingField.cropzones.push(cz);
      }
    });
  };

  const addNewFieldWithCropZones = (
    updatedFields,
    field,
    cropZonesToAdd,
    farm
  ) => {
    updatedFields.push({
      ...field,
      farmName: farm ? farm.name : field.farmName,
      farmId: farm ? farm.id : field.farmId,
      cropzones: cropZonesToAdd
    });
  };

  const updateFieldInCropGroup = (
    updatedFields,
    field,
    farm,
    crop,
    allSelected
  ) => {
    const cropzonesToHandle = field.cropzones.filter(
      cz => cz.crop.id === crop.id
    );
    const fieldIndex = updatedFields.findIndex(f => f.id === field.id);
    if (fieldIndex >= 0) {
      const existingField = updatedFields[fieldIndex];
      if (allSelected) {
        removeCropZonesFromField(existingField, crop.id);
        if (existingField.cropzones.length === 0) {
          updatedFields.splice(fieldIndex, 1);
        }
      } else {
        addCropZonesToField(existingField, cropzonesToHandle);
      }
    } else if (!allSelected) {
      addNewFieldWithCropZones(updatedFields, field, cropzonesToHandle, farm);
    }
  };

  // Functions for grouping by farms
  const handleSelectedFarm = useCallback(
    farm => {
      if (isSortByCrop) return;
      let updatedFields = [...fieldsToMassiveAssign];
      const allFieldsSelected = farm.fields.every(field =>
        isFieldFullySelected(field)
      );
      if (allFieldsSelected) {
        updatedFields = updatedFields.filter(
          f => !farm.fields.some(field => field.id === f.id)
        );
      } else {
        farm.fields.forEach(field => {
          const fieldIndex = updatedFields.findIndex(f => f.id === field.id);
          if (fieldIndex >= 0) {
            updatedFields[fieldIndex].cropzones = [...field.cropzones];
          } else {
            updatedFields.push({
              ...field,
              farmName: farm.name,
              farmId: farm.id,
              cropzones: [...field.cropzones]
            });
          }
        });
      }
      dispatch({
        type: FIELDS_TO_MASSIVE_ASSIGN,
        payload: updatedFields
      });
    },
    [isSortByCrop, fieldsToMassiveAssign, isFieldFullySelected, dispatch]
  );

  const isFieldSelected = useCallback(
    field => {
      if (isSortByCrop) return false;
      return isFieldFullySelected(field);
    },
    [isSortByCrop, isFieldFullySelected]
  );

  const isFarmSelected = useCallback(
    farm => {
      if (isSortByCrop) return false;
      return farm.fields.every(field => isFieldSelected(field));
    },
    [isSortByCrop, isFieldSelected]
  );

  const handleSelectedField = useCallback(
    (farm, field) => {
      if (isSortByCrop) return;
      const updatedFields = [...fieldsToMassiveAssign];
      const fieldIndex = updatedFields.findIndex(f => f.id === field.id);
      const fieldInAssign = updatedFields[fieldIndex];
      const allCropZonesSelected = fieldInAssign
        ? field.cropzones.every(cz =>
            fieldInAssign.cropzones.some(fcz => fcz.id === cz.id)
          )
        : false;
      if (fieldInAssign) {
        if (allCropZonesSelected) {
          updatedFields.splice(fieldIndex, 1);
        } else {
          updatedFields[fieldIndex].cropzones = [...field.cropzones];
        }
      } else {
        updatedFields.push({
          ...field,
          farmName: farm.name,
          farmId: farm.id,
          cropzones: [...field.cropzones]
        });
      }
      dispatch({
        type: FIELDS_TO_MASSIVE_ASSIGN,
        payload: updatedFields
      });
    },
    [isSortByCrop, fieldsToMassiveAssign, dispatch]
  );

  // Functions for grouping by crops
  const isCropGroupSelected = useCallback(
    crop => {
      if (!isSortByCrop) return false;
      return isCropGroupFullySelected(crop);
    },
    [isSortByCrop, isCropGroupFullySelected]
  );

  const isCropZoneSelected = useCallback(
    cropzone => {
      // eslint-disable-next-line no-restricted-syntax
      for (const [, czSet] of selectedFieldMap) {
        if (czSet.has(cropzone.id)) return true;
      }
      return false;
    },
    [selectedFieldMap]
  );

  const isPropertyIndeterminate = useCallback(
    property => {
      if (isSortByCrop) return false;
      if (property.fields) {
        const someSelected = property.fields.some(
          field =>
            isFieldSelected(field) ||
            field.cropzones.some(cz => isCropZoneSelected(cz))
        );
        const allSelected = isFarmSelected(property);
        return someSelected && !allSelected;
      }
      if (property.cropzones) {
        const someSelected = property.cropzones.some(cz =>
          isCropZoneSelected(cz)
        );
        const allSelected = property.cropzones.every(cz =>
          isCropZoneSelected(cz)
        );
        return someSelected && !allSelected;
      }
      return false;
    },
    [isSortByCrop, isFieldSelected, isCropZoneSelected, isFarmSelected]
  );

  const isPropertyIndeterminateInCropGroup = useCallback(
    (crop, property) => {
      if (!isSortByCrop) return false;
      if (property.fields) {
        const someSelected = property.fields.some(
          field =>
            isFieldSelectedInCropGroup(crop, field) ||
            field.cropzones.some(cz => isCropZoneSelected(cz))
        );
        const allSelected = isFarmSelectedInCropGroup(crop, property);
        return someSelected && !allSelected;
      }
      if (property.cropzones) {
        const cropZonesToCheck = property.cropzones.filter(
          cz => cz.crop.id === crop.id
        );
        const someSelected = cropZonesToCheck.some(cz =>
          isCropZoneSelected(cz)
        );
        const allSelected =
          cropZonesToCheck.length > 0 &&
          cropZonesToCheck.every(cz => isCropZoneSelected(cz));
        return someSelected && !allSelected;
      }
      return false;
    },
    [
      isSortByCrop,
      isFieldSelectedInCropGroup,
      isCropZoneSelected,
      isFarmSelectedInCropGroup
    ]
  );

  const allPropertiesIndeterminate = useCallback(() => {
    if (isSortByCrop) {
      const someSelected = processedProperties.some(cropGroup => {
        const crop = { id: cropGroup.id, name: cropGroup.name };
        return cropGroup.farms.some(
          farm =>
            (farm?.fields?.length && isFarmSelectedInCropGroup(crop, farm)) ||
            isPropertyIndeterminateInCropGroup(crop, farm)
        );
      });
      const allSelected = processedProperties.every(cropGroup => {
        const crop = { id: cropGroup.id, name: cropGroup.name };
        return cropGroup.farms.every(
          farm =>
            (farm?.fields?.length && isFarmSelectedInCropGroup(crop, farm)) ||
            !farm?.fields?.length
        );
      });
      return someSelected && !allSelected;
    }

    if (!processedProperties || processedProperties.length === 0) return false;
    const someSelected = processedProperties.some(property => {
      return (
        (property.fields?.length && isFarmSelected(property)) ||
        isPropertyIndeterminate(property)
      );
    });
    const allSelected = processedProperties.every(property =>
      isFarmSelected(property)
    );
    return someSelected && !allSelected;
  }, [
    isSortByCrop,
    processedProperties,
    isFarmSelectedInCropGroup,
    isPropertyIndeterminateInCropGroup,
    isFarmSelected,
    isPropertyIndeterminate
  ]);

  const handleSelectedCropGroup = useCallback(
    crop => {
      if (!isSortByCrop) return;
      const allSelected = isCropGroupFullySelected(crop);
      const updatedFields = [...fieldsToMassiveAssign];
      crop.farms.forEach(farm => {
        farm.fields.forEach(field => {
          updateFieldInCropGroup(updatedFields, field, farm, crop, allSelected);
        });
      });
      dispatch({ type: FIELDS_TO_MASSIVE_ASSIGN, payload: updatedFields });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSortByCrop, isCropGroupFullySelected, fieldsToMassiveAssign, dispatch]
  );

  const handleSelectedFarmInCropGroup = useCallback(
    (crop, farm) => {
      if (!isSortByCrop) return;
      const allSelected = isFarmSelectedInCropGroup(crop, farm);
      const updatedFields = [...fieldsToMassiveAssign];
      farm.fields.forEach(field => {
        updateFieldInCropGroup(updatedFields, field, farm, crop, allSelected);
      });
      dispatch({ type: FIELDS_TO_MASSIVE_ASSIGN, payload: updatedFields });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSortByCrop, isFarmSelectedInCropGroup, fieldsToMassiveAssign, dispatch]
  );

  const handleSelectedFieldInCropGroup = useCallback(
    (crop, field) => {
      if (!isSortByCrop) return;
      const allSelected = isFieldSelectedInCropGroup(crop, field);
      const updatedFields = [...fieldsToMassiveAssign];
      updateFieldInCropGroup(updatedFields, field, null, crop, allSelected);
      dispatch({ type: FIELDS_TO_MASSIVE_ASSIGN, payload: updatedFields });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSortByCrop, isFieldSelectedInCropGroup, fieldsToMassiveAssign, dispatch]
  );

  const handleSelectedCropZone = useCallback(
    (farm, field, cropZone) => {
      const updatedFields = [...fieldsToMassiveAssign];
      const fieldIndex = updatedFields.findIndex(fd => fd.id === field.id);
      if (fieldIndex >= 0) {
        const fieldInAssign = updatedFields[fieldIndex];
        const cropZoneIndex = fieldInAssign.cropzones.findIndex(
          cz => cz.id === cropZone.id
        );
        if (cropZoneIndex >= 0) {
          fieldInAssign.cropzones.splice(cropZoneIndex, 1);
          if (fieldInAssign.cropzones.length === 0) {
            updatedFields.splice(fieldIndex, 1);
          }
        } else {
          fieldInAssign.cropzones.push(cropZone);
        }
      } else {
        updatedFields.push({
          ...field,
          farmName: farm.name,
          farmId: farm.id,
          cropzones: [cropZone]
        });
      }
      dispatch({
        type: FIELDS_TO_MASSIVE_ASSIGN,
        payload: updatedFields
      });
    },
    [fieldsToMassiveAssign, dispatch]
  );

  const isCropGroupIndeterminate = useCallback(
    crop => {
      if (!crop.farms || crop.farms.length === 0) return false;
      const allSelected = crop.farms.every(farm =>
        isFarmSelectedInCropGroup(crop, farm)
      );
      const someSelected = crop.farms.some(
        farm =>
          isFarmSelectedInCropGroup(crop, farm) ||
          isPropertyIndeterminateInCropGroup(crop, farm)
      );
      return someSelected && !allSelected;
    },
    [isFarmSelectedInCropGroup, isPropertyIndeterminateInCropGroup]
  );

  return {
    handleSelectAllProperties,
    isFieldSelectedInCropGroup,
    isFarmSelectedInCropGroup,
    isCropGroupFullySelected,
    isFarmSelected,
    isFieldSelected,
    isCropZoneSelected,
    handleSelectedFarm,
    handleSelectedField,
    isCropGroupSelected,
    handleSelectedCropGroup,
    handleSelectedFarmInCropGroup,
    handleSelectedFieldInCropGroup,
    handleSelectedCropZone,
    allPropertiesSelected,
    isPropertyIndeterminate,
    isPropertyIndeterminateInCropGroup,
    allPropertiesIndeterminate,
    isCropGroupIndeterminate
  };
};

export default useMassAssignerTools;
